import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { api } from "@/lib/api.js";

type Status = "pending" | "success" | "error" | "invalid";

export function VerifyEmailPage() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token");
  const [status, setStatus] = useState<Status>("pending");
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  useEffect(() => {
    if (!token) { setStatus("invalid"); return; }
    api
      .post<{ ok: boolean }>("/api/auth/verify-email", { token })
      .then(() => setStatus("success"))
      .catch((e) => {
        setStatus("error");
        setErrorMsg(e instanceof Error ? e.message : "인증에 실패했습니다.");
      });
  }, [token]);

  if (status === "pending") {
    return (
      <Center>
        <div className="h-8 w-8 animate-spin rounded-full border-2 border-brand-500 border-t-transparent" />
        <p className="mt-4 text-sm text-gray-500">이메일 인증 중...</p>
      </Center>
    );
  }

  if (status === "success") {
    return (
      <Center>
        <motion.div
          initial={{ scale: 0 }}
          animate={{ scale: 1 }}
          className="flex h-16 w-16 items-center justify-center rounded-full bg-green-100 text-3xl"
        >
          ✓
        </motion.div>
        <motion.div
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="mt-4 text-center"
        >
          <h1 className="text-xl font-bold text-gray-900">이메일 인증 완료</h1>
          <p className="mt-2 text-sm text-gray-500">이제 로그인하실 수 있습니다.</p>
          <a
            href="/login"
            className="mt-4 inline-block rounded-xl bg-brand-500 px-6 py-2.5 text-sm font-semibold text-white hover:bg-brand-600"
          >
            로그인하기
          </a>
        </motion.div>
      </Center>
    );
  }

  if (status === "invalid") {
    return (
      <Center>
        <div className="flex h-16 w-16 items-center justify-center rounded-full bg-red-50 text-3xl">⚠</div>
        <div className="mt-4 text-center">
          <h1 className="text-xl font-bold text-gray-900">유효하지 않은 링크</h1>
          <p className="mt-2 text-sm text-gray-500">인증 링크가 없거나 잘못되었습니다.</p>
        </div>
      </Center>
    );
  }

  return (
    <Center>
      <div className="flex h-16 w-16 items-center justify-center rounded-full bg-red-50 text-3xl">✕</div>
      <div className="mt-4 text-center">
        <h1 className="text-xl font-bold text-gray-900">인증 실패</h1>
        <p className="mt-2 text-sm text-gray-500">{errorMsg ?? "링크가 만료되었거나 이미 사용되었습니다."}</p>
        <p className="mt-4 rounded-lg bg-gray-50 px-4 py-2 text-xs text-gray-500">
          관리자에게 인증 메일 재발송을 요청해 주세요.
        </p>
      </div>
    </Center>
  );
}

function Center({ children }: { children: React.ReactNode }) {
  return (
    <div className="flex min-h-screen flex-col items-center justify-center px-4">
      {children}
    </div>
  );
}
